import React, { useState, useEffect, useRef } from 'react';
import { Animated, Easing } from 'react-native';
import CheckinSquares from './components/CheckinSquares';
import { api } from '../../../services/api';
import { Container, StrakIcon, Streak, StreakText, StreakTitle, StreakTitleContainer } from './styles';
import { Button } from '../../Interface/Forms/Buttons/Button';
import theme from '../../../global/styles/legacy/theme';
import CheckinWeeks from './components/CheckinWeeks';
import { CheckinStatus } from './types';

interface CheckinComponentProps {
  data: CheckinStatus;
  onCheckin: (data: any) => Promise<any>;
}

const CheckinComponent: React.FC<CheckinComponentProps> = ({ data, onCheckin }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);

  const heartbeatAnim = useRef(new Animated.Value(1)).current;
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const animation = Animated.sequence([
    Animated.timing(heartbeatAnim, {
      toValue: 0.92,
      duration: 600,
      easing: Easing.inOut(Easing.ease),
      useNativeDriver: true,
    }),
    Animated.timing(heartbeatAnim, {
      toValue: 1,
      duration: 600,
      easing: Easing.inOut(Easing.ease),
      useNativeDriver: true,
    }),
  ]);

  const convertTimeToSecondsSafe = (time?: string): number => {
    if (!time) return 0;
    const m = /^(\d{1,2}):(\d{1,2}):(\d{1,2})$/.exec(time.trim());
    if (!m) return 0;
    const hrs = Number(m[1]) || 0;
    const mins = Number(m[2]) || 0;
    const secs = Number(m[3]) || 0;
    return hrs * 3600 + mins * 60 + secs;
  };

  const formatSeconds = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const mins = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${hrs}:${mins}:${secs}`;
  };

  // Conta-dor baseado somente no tempo_restante da API
  useEffect(() => {
    // limpa qualquer intervalo anterior
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const initialSeconds = convertTimeToSecondsSafe(data?.tempo_restante);
    setRemainingSeconds(initialSeconds);

    if (initialSeconds > 0) {
      timerRef.current = setInterval(() => {
        setRemainingSeconds(prev => {
          if (prev <= 1) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [data?.tempo_restante]);

  const handleCheckin = async () => {
    setLoading(true);
    try {
      const currentDate = new Date().toISOString();
      const { data: resp } = await api.post('/grava_checkin', { date: currentDate });
      await onCheckin(resp);
      // Dica: se o backend já retornar o novo tempo_restante, o efeito acima ressincroniza sozinho
    } catch (error) {
      console.error('Erro ao realizar checkin:', error);
    } finally {
      setLoading(false);
    }
  };

  const convertDaysToInlineDate = (total: number) => {
    const weeks = Math.floor(total / 7);
    const days = total % 7;
    return { weeks, days };
  };

  if (!data) return <></>;

  const { days, weeks } = convertDaysToInlineDate(Number(data.checkin));

  let buttonTitle = 'Fazer Check-in';
  if (loading) {
    buttonTitle = 'Realizando checkin...';
  } else if (!data.checkin_liberado) {
    buttonTitle = `Disponível em: ${formatSeconds(remainingSeconds)}`;
  }

  useEffect(() => {
    Animated.loop(animation).start();
  }, []);

  return (
    <Container>
      <Streak>
        <StreakTitleContainer>
          <StrakIcon name="calendar-week" />
          <StreakTitle>
            {weeks === 0 && days === 0 ? (
              '1° dia'
            ) : (
              <>
                {weeks > 0 ? 'Você está na ' : ''}
                {weeks > 0 ? `${weeks} semana` : ''}
                {weeks > 0 && days > 0 ? ' e ' : ' '}
                {days > 0 ? `${days}° dia${days > 1 ? 's' : ''}` : ''}
              </>
            )}
          </StreakTitle>
        </StreakTitleContainer>

        <StreakText>
          {weeks === 0 && days === 0
            ? 'Faça o check-in para ganhar baratinhaZ!'
            : 'Para ganhar mais baratinhaZ, volte amanhã e faça o check-in.'}
        </StreakText>
      </Streak>

      <CheckinSquares data_checkin={data.data_checkin} streak={data.checkin} />
      <CheckinWeeks weeks={weeks} points={data.pontos} />

      <Animated.View style={{ transform: [{ scale: heartbeatAnim }] }}>
        <Button
          color={theme.colors.orange}
          onPress={handleCheckin}
          enabled={!loading && data.checkin_liberado}
          title={buttonTitle}
          loading={loading}
          shadow
        />
      </Animated.View>
    </Container>
  );
};

export default CheckinComponent;
